import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  makeStyles,
  tokens,
  Title1,
  Title3,
  Card,
  CardHeader,
  Text,
  Button,
  Spinner,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Badge,
  shorthands,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Field,
  Textarea,
  Input,
  Combobox,
  Option,
} from "@fluentui/react-components";
import {
  CheckmarkCircle24Regular,
  DismissCircle24Regular,
  Clock24Regular,
  Eye24Regular,
  Checkmark24Regular,
  Dismiss24Regular,
  Search24Regular,
  FilterDismiss24Regular,
} from "@fluentui/react-icons";
import { useApiClient } from "../hooks/useApiClient";

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap("24px"),
    ...shorthands.padding("24px"),
    width: "100%",
    maxWidth: "1400px",
    marginLeft: "auto",
    marginRight: "auto",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    ...shorthands.gap("16px"),
  },
  headerTitle: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    ...shorthands.gap("16px"),
  },
  statCard: {
    height: "100%",
  },
  statCardHeader: {
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap("8px"),
  },
  statValue: {
    fontSize: tokens.fontSizeHero700,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorBrandForeground1,
  },
  statLabel: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground2,
  },
  requestsSection: {
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap("16px"),
  },
  requestsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))",
    ...shorthands.gap("16px"),
  },
  requestCard: {
    height: "100%",
    cursor: "pointer",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  requestHeader: {
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap("8px"),
  },
  requestTitle: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
  },
  requestMeta: {
    display: "flex",
    alignItems: "center",
    ...shorthands.gap("12px"),
    flexWrap: "wrap",
  },
  requestDetail: {
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap("4px"),
  },
  detailLabel: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  detailValue: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    ...shorthands.padding("48px"),
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    gap: tokens.spacingVerticalM,
    ...shorthands.padding("48px"),
  },
  adminBadge: {
    backgroundColor: tokens.colorPaletteRedBackground3,
    color: tokens.colorNeutralForegroundOnBrand,
  },
  filterBar: {
    display: "flex",
    alignItems: "flex-end",
    flexWrap: "wrap",
    ...shorthands.gap("16px"),
    ...shorthands.padding("16px"),
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
  },
  filterCount: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    alignSelf: "center",
  },
  settingsCard: {
    display: "flex",
    alignItems: "flex-end",
    flexWrap: "wrap",
    gap: tokens.spacingHorizontalM,
  },
});

interface ApprovalRequest {
  supplier?: string;
  erpPaymentTerms?: string | null;
  id: string;
  orderNumber: string;
  orderType: string;
  requestedBy: string;
  requestedByName: string;
  purchasingManager: string;
  portfolioCfroi: number;
  totalInvestment: number;
  status: string;
  createdAt: string;
  expiresAt: string;
}

interface Statistics {
  total_requests: number;
  pending_requests: number;
  approved_requests: number;
  rejected_requests: number;
  expired_requests: number;
  avg_cfroi: number;
  total_investment: number;
}

export default function AdminDashboardPage() {
  const styles = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    getAdminApprovals,
    approveRequest,
    rejectRequest,
  } = useApiClient();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [requests, setRequests] = useState<ApprovalRequest[]>([]);
  const [processingRequestId, setProcessingRequestId] = useState<string | null>(
    null
  );
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Reject dialog state
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(
    null
  );
  const [rejectReason, setRejectReason] = useState("");
  const [rejectNotes, setRejectNotes] = useState("");

  // Filter state — default to last 7 days
  const getDefaultDateFrom = useCallback(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().slice(0, 10);
  }, []);
  const getDefaultDateTo = useCallback(() => new Date().toISOString().slice(0, 10), []);

  const [filterPurchaser, setFilterPurchaser] = useState(() => searchParams.get("purchaser") ?? "");
  const [filterSupplier, setFilterSupplier] = useState(() => searchParams.get("supplier") ?? "");
  const [suppliers, setSuppliers] = useState<string[]>([]);
  const [supplierSuggestionsOpen, setSupplierSuggestionsOpen] = useState(false);
  const supplierSuggestions = useMemo(() => {
    const query = filterSupplier.trim().toLowerCase();
    if (query.length < 3) return [];
    return suppliers.filter(name => name.toLowerCase().includes(query)).slice(0, 10);
  }, [suppliers, filterSupplier]);
  const [filterDateFrom, setFilterDateFrom] = useState(() => searchParams.get("from") ?? getDefaultDateFrom());
  const [filterDateTo, setFilterDateTo] = useState(() => searchParams.get("to") ?? getDefaultDateTo());
  const restoreScroll = useRef(true);
  useEffect(() => {
    if (loading || !restoreScroll.current) return;
    restoreScroll.current = false;
    const key = "admin-scroll:" + location.search;
    const saved = sessionStorage.getItem(key);
    if (saved === null) return;
    sessionStorage.removeItem(key);
    const frame = requestAnimationFrame(() => window.scrollTo(0, Number(saved) || 0));
    return () => cancelAnimationFrame(frame);
  }, [loading, location.search]);

  // Ref for cancelling in-flight requests when filters change
  const abortControllerRef = useRef<AbortController | null>(null);

  // Calculate statistics from fetched requests
  const statistics: Statistics = useMemo(() => {
    const isPendingStatus = (s: string) => s.toLowerCase() === "pending" || s.toLowerCase() === "awaiting_second_approval";
    const pending = requests.filter(r => isPendingStatus(r.status));
    const approved = requests.filter(r => r.status.toLowerCase() === "approved");
    const rejected = requests.filter(r => r.status.toLowerCase() === "rejected");
    const expired = requests.filter(r => r.status.toLowerCase() === "expired" || r.status.toLowerCase() === "timeout");

    const totalInvestment = pending.reduce((sum, r) => sum + r.totalInvestment, 0);
    const avgCfroi = pending.length > 0
      ? pending.reduce((sum, r) => sum + r.portfolioCfroi, 0) / pending.length
      : 0;

    return {
      total_requests: requests.length,
      pending_requests: pending.length,
      approved_requests: approved.length,
      rejected_requests: rejected.length,
      expired_requests: expired.length,
      avg_cfroi: avgCfroi,
      total_investment: totalInvestment,
    };
  }, [requests]);

  const isFiltered =
    filterSupplier.trim() !== "" ||
    filterPurchaser.trim() !== "" ||
    filterDateFrom !== getDefaultDateFrom() ||
    filterDateTo !== getDefaultDateTo();

  const clearFilters = () => {
    const from = getDefaultDateFrom();
    const to = getDefaultDateTo();
    setFilterPurchaser("");
    setFilterSupplier("");
    setSupplierSuggestionsOpen(false);
    setFilterDateFrom(from);
    setFilterDateTo(to);
    fetchApprovals(from, to, "", "");
  };

  const handleSearch = () => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    fetchApprovals(filterDateFrom, filterDateTo, filterPurchaser, filterSupplier, controller.signal);
  };

  // Re-fetch when date filters change (immediate)
  const handleDateFromChange = (_: unknown, data: { value: string }) => {
    setFilterDateFrom(data.value);
  };

  const handleDateToChange = (_: unknown, data: { value: string }) => {
    setFilterDateTo(data.value);
  };

  const handlePurchaserChange = (_: unknown, data: { value: string }) => {
    setFilterPurchaser(data.value);
  };

  const fetchApprovals = useCallback(async (
    dateFrom: string,
    dateTo: string,
    requesterName: string,
    supplier: string,
    signal?: AbortSignal,
    preferCache = false
  ) => {
    setSearchParams({ from: dateFrom, to: dateTo, purchaser: requesterName, supplier }, { replace: true });
    setLoading(true);
    setError(null);
    try {
      const requestsData = await getAdminApprovals({ dateFrom, dateTo, requesterName: requesterName.trim(), supplier: supplier.trim() }, signal, preferCache);
      if (signal?.aborted) return;
      setRequests(requestsData.requests || []);
      setSuppliers(requestsData.suppliers ?? []);
    } catch (err: any) {
      if (err.name === "CanceledError" || err.name === "AbortError") return;
      console.error("Failed to load admin data:", err);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to load admin dashboard data"
      );
    } finally {
      setLoading(false);
    }
  }, [getAdminApprovals, setSearchParams]);

  useEffect(() => {
    const controller = new AbortController();
    abortControllerRef.current = controller;
    fetchApprovals(filterDateFrom, filterDateTo, filterPurchaser, filterSupplier, controller.signal, true);
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApprove = async (requestId: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent card click navigation

    if (!confirm("Are you sure you want to approve this request?")) {
      return;
    }

    setProcessingRequestId(requestId);
    setError(null);
    setSuccessMessage(null);

    try {
      const result = await approveRequest(requestId);
      setSuccessMessage(result.message || "Request approved successfully");

      // Reload data to refresh the list
      await fetchApprovals(filterDateFrom, filterDateTo, filterPurchaser, filterSupplier);
    } catch (err: any) {
      console.error("Failed to approve request:", err);
      setError(
        err.response?.data?.error || err.message || "Failed to approve request"
      );
    } finally {
      setProcessingRequestId(null);
    }
  };

  const handleRejectClick = (requestId: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent card click navigation
    setSelectedRequestId(requestId);
    setRejectDialogOpen(true);
    setRejectReason("");
    setRejectNotes("");
  };

  const handleRejectConfirm = async () => {
    if (!selectedRequestId || !rejectReason.trim()) {
      setError("Please provide a reason for rejection");
      return;
    }

    setProcessingRequestId(selectedRequestId);
    setError(null);
    setSuccessMessage(null);
    setRejectDialogOpen(false);

    try {
      const result = await rejectRequest(
        selectedRequestId,
        rejectReason,
        rejectNotes || undefined
      );
      setSuccessMessage(result.message || "Request rejected successfully");

      // Reload data to refresh the list
      await fetchApprovals(filterDateFrom, filterDateTo, filterPurchaser, filterSupplier);
    } catch (err: any) {
      console.error("Failed to reject request:", err);
      setError(
        err.response?.data?.error || err.message || "Failed to reject request"
      );
    } finally {
      setProcessingRequestId(null);
      setSelectedRequestId(null);
      setRejectReason("");
      setRejectNotes("");
    }
  };

  const handleRejectCancel = () => {
    setRejectDialogOpen(false);
    setSelectedRequestId(null);
    setRejectReason("");
    setRejectNotes("");
  };

  const handleViewDetails = (requestId: string) => {
    sessionStorage.setItem("admin-scroll:" + location.search, String(window.scrollY));
    navigate(`/details/${requestId}`, { state: { adminReturnTo: "/admin" + location.search } });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
    }).format(amount);
  };

  const formatPercentage = (value: number) => {
    return `${(value * 100).toFixed(2)}%`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("de-DE", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case "approved":
        return <CheckmarkCircle24Regular />;
      case "rejected":
        return <DismissCircle24Regular />;
      case "awaiting_second_approval":
        return <Checkmark24Regular />;
      default:
        return <Clock24Regular />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "approved":
        return "success";
      case "rejected":
        return "danger";
      case "awaiting_second_approval":
        return "informative";
      default:
        return "warning";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status.toLowerCase()) {
      case "pending":
        return "Awaiting Level 1 Approval";
      case "awaiting_second_approval":
        return "Awaiting Level 2 Approval";
      case "approved":
        return "Approved";
      case "rejected":
        return "Rejected";
      case "timeout":
      case "expired":
        return "Expired";
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size="large" label="Loading admin dashboard..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>Error</MessageBarTitle>
            {error}
          </MessageBarBody>
        </MessageBar>
        <Button onClick={() => fetchApprovals(filterDateFrom, filterDateTo, filterPurchaser, filterSupplier)}>Retry</Button>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Success Message */}
      {successMessage && (
        <MessageBar intent="success">
          <MessageBarBody>
            <MessageBarTitle>Success</MessageBarTitle>
            {successMessage}
          </MessageBarBody>
        </MessageBar>
      )}

      {/* Error Message */}
      {error && (
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>Error</MessageBarTitle>
            {error}
          </MessageBarBody>
        </MessageBar>
      )}

      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerTitle}>
          <Title1>Admin Dashboard</Title1>
          <Text>View and manage pending and recent purchase approval requests</Text>
        </div>
        <Badge
          appearance="filled"
          color="danger"
          size="large"
          className={styles.adminBadge}
        >
          Admin View
        </Badge>
      </div>

      {/* Statistics Cards */}
      <div className={styles.statsGrid}>
        <Card className={styles.statCard}>
          <CardHeader
            header={
              <div className={styles.statCardHeader}>
                <div className={styles.statValue}>
                  {statistics.pending_requests}
                </div>
                <div className={styles.statLabel}>Pending Requests</div>
              </div>
            }
          />
        </Card>

        <Card className={styles.statCard}>
          <CardHeader
            header={
              <div className={styles.statCardHeader}>
                <div className={styles.statValue}>
                  {formatPercentage(statistics.avg_cfroi)}
                </div>
                <div className={styles.statLabel}>Average CFROI</div>
              </div>
            }
          />
        </Card>

        <Card className={styles.statCard}>
          <CardHeader
            header={
              <div className={styles.statCardHeader}>
                <div className={styles.statValue}>
                  {formatCurrency(statistics.total_investment)}
                </div>
                <div className={styles.statLabel}>Total Investment</div>
              </div>
            }
          />
        </Card>

        <Card className={styles.statCard}>
          <CardHeader
            header={
              <div className={styles.statCardHeader}>
                <div className={styles.statValue}>
                  {statistics.total_requests}
                </div>
                <div className={styles.statLabel}>Total Requests</div>
              </div>
            }
          />
        </Card>
      </div>

      {/* Approval Requests Section */}
      <div className={styles.requestsSection}>
        {/* Filter Bar */}
        <div className={styles.filterBar}>
          <Field label="Purchaser">
            <Input
              placeholder="Filter by name or email..."
              value={filterPurchaser}
              onChange={handlePurchaserChange}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              contentBefore={<Search24Regular />}
              style={{ minWidth: "220px" }}
            />
          </Field>
          <Field label="From Date">
            <Input
              type="date"
              value={filterDateFrom}
              onChange={handleDateFromChange}
            />
          </Field>
          <Field label="Supplier">
            <Combobox
              aria-label="Supplier"
              freeform
              placeholder="Type at least 3 characters..."
              open={supplierSuggestionsOpen && supplierSuggestions.length > 0}
              onOpenChange={(_, data) => setSupplierSuggestionsOpen(data.open)}
              value={filterSupplier}
              selectedOptions={suppliers.includes(filterSupplier) ? [filterSupplier] : []}
              onChange={(event) => {
                setFilterSupplier(event.target.value);
                setSupplierSuggestionsOpen(event.target.value.trim().length >= 3);
              }}
              onOptionSelect={(_, data) => {
                setFilterSupplier(data.optionText ?? "");
                setSupplierSuggestionsOpen(false);
              }}
              style={{ minWidth: "220px" }}
            >
              {supplierSuggestions.map(name => (
                <Option key={name} value={name}>{name}</Option>
              ))}
            </Combobox>
          </Field>
          <Field label="To Date">
            <Input
              type="date"
              value={filterDateTo}
              onChange={handleDateToChange}
            />
          </Field>
          <Button
            appearance="primary"
            icon={<Search24Regular />}
            onClick={handleSearch}
            style={{ alignSelf: "flex-end" }}
          >
            Search
          </Button>
          <Button onClick={handleSearch}>Refresh</Button>
          {isFiltered && (
            <Button
              appearance="subtle"
              icon={<FilterDismiss24Regular />}
              onClick={clearFilters}
              style={{ alignSelf: "flex-end" }}
            >
              Clear Filters
            </Button>
          )}
          {isFiltered && (
            <Text className={styles.filterCount}>
              {requests.length} requests shown
            </Text>
          )}
        </div>

        <Title3>Pending Approval Requests</Title3>

        {requests.filter(r => r.status.toLowerCase() === "pending" || r.status.toLowerCase() === "awaiting_second_approval").length === 0 ? (
          <div className={styles.emptyState}>
            <CheckmarkCircle24Regular
              style={{
                fontSize: "48px",
                color: tokens.colorNeutralForeground3,
              }}
            />
            <Title3>No Pending Requests</Title3>
            <Text>All approval requests have been processed</Text>
          </div>
        ) : (
          <div className={styles.requestsGrid}>
            {requests.filter(r => r.status.toLowerCase() === "pending" || r.status.toLowerCase() === "awaiting_second_approval").map((request) => (
              <Card
                key={request.id}
                className={styles.requestCard}
                onClick={() => handleViewDetails(request.id)}
              >
                <CardHeader
                  header={
                    <div className={styles.requestHeader}>
                      <div className={styles.requestTitle}>
                        Order #{request.orderNumber}
                      </div>
                      <div className={styles.requestMeta}>
                        <Badge
                          appearance="outline"
                          color={getStatusColor(request.status)}
                          icon={getStatusIcon(request.status)}
                        >
                          {getStatusLabel(request.status)}
                        </Badge>
                        <Badge appearance="outline">{request.orderType}</Badge>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          gap: "16px",
                          marginTop: "8px",
                        }}
                      >
                        <div className={styles.requestDetail}>
                          <div className={styles.detailLabel}>Requested By</div>
                          <div className={styles.detailValue}>
                            {request.requestedByName}
                          </div>
                        </div>

                        <div className={styles.requestDetail}>
                          <div className={styles.detailLabel}>Manager</div>
                          <div className={styles.detailValue}>
                            {request.purchasingManager}
                          </div>
                        </div>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          gap: "16px",
                          marginTop: "8px",
                        }}
                      >
                        <div className={styles.requestDetail}>
                          <div className={styles.detailLabel}>CFROI</div>
                          <div className={styles.detailValue}>
                            {formatPercentage(request.portfolioCfroi)}
                          </div>
                        </div>

                        <div className={styles.requestDetail}>
                          <div className={styles.detailLabel}>Investment</div>
                          <div className={styles.detailValue}>
                            {formatCurrency(request.totalInvestment)}
                          </div>
                        </div>
                      </div>

                      <div style={{ marginTop: "12px" }}>
                        <Text
                          size={200}
                          style={{ color: tokens.colorNeutralForeground2 }}
                        >
                          Created: {formatDate(request.createdAt)}
                        </Text>
                      </div>

                      {/* Action Buttons for Pending */}
                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          marginTop: "12px",
                          flexWrap: "wrap",
                        }}
                      >
                        <Button
                          appearance="primary"
                          icon={<Checkmark24Regular />}
                          onClick={(e) => handleApprove(request.id, e)}
                          disabled={processingRequestId === request.id}
                        >
                          {processingRequestId === request.id
                            ? "Processing..."
                            : "Approve"}
                        </Button>
                        <Button
                          appearance="secondary"
                          icon={<Dismiss24Regular />}
                          onClick={(e) => handleRejectClick(request.id, e)}
                          disabled={processingRequestId === request.id}
                        >
                          Reject
                        </Button>
                        <Button
                          appearance="subtle"
                          icon={<Eye24Regular />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewDetails(request.id);
                          }}
                        >
                          View Details
                        </Button>
                      </div>
                    </div>
                  }
                />
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Recent Requests Section */}
      {requests.filter(r => r.status.toLowerCase() !== "pending" && r.status.toLowerCase() !== "awaiting_second_approval").length > 0 && (
        <div className={styles.requestsSection}>
          <Title3>Recent Requests</Title3>

          <div className={styles.requestsGrid}>
            {requests.filter(r => r.status.toLowerCase() !== "pending" && r.status.toLowerCase() !== "awaiting_second_approval").map((request) => (
              <Card
                key={request.id}
                className={styles.requestCard}
                onClick={() => handleViewDetails(request.id)}
              >
                <CardHeader
                  header={
                    <div className={styles.requestHeader}>
                      <div className={styles.requestTitle}>
                        Order #{request.orderNumber}
                      </div>
                      <div className={styles.requestMeta}>
                        <Badge
                          appearance="outline"
                          color={getStatusColor(request.status)}
                          icon={getStatusIcon(request.status)}
                        >
                          {getStatusLabel(request.status)}
                        </Badge>
                        <Badge appearance="outline">{request.orderType}</Badge>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          gap: "16px",
                          marginTop: "8px",
                        }}
                      >
                        <div className={styles.requestDetail}>
                          <div className={styles.detailLabel}>Requested By</div>
                          <div className={styles.detailValue}>
                            {request.requestedByName}
                          </div>
                        </div>

                        <div className={styles.requestDetail}>
                          <div className={styles.detailLabel}>Manager</div>
                          <div className={styles.detailValue}>
                            {request.purchasingManager}
                          </div>
                        </div>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          gap: "16px",
                          marginTop: "8px",
                        }}
                      >
                        <div className={styles.requestDetail}>
                          <div className={styles.detailLabel}>CFROI</div>
                          <div className={styles.detailValue}>
                            {formatPercentage(request.portfolioCfroi)}
                          </div>
                        </div>

                        <div className={styles.requestDetail}>
                          <div className={styles.detailLabel}>Investment</div>
                          <div className={styles.detailValue}>
                            {formatCurrency(request.totalInvestment)}
                          </div>
                        </div>
                      </div>

                      <div style={{ marginTop: "12px" }}>
                        <Text
                          size={200}
                          style={{ color: tokens.colorNeutralForeground2 }}
                        >
                          Created: {formatDate(request.createdAt)}
                        </Text>
                      </div>

                      {/* View Details button for non-pending */}
                      <Button
                        appearance="subtle"
                        icon={<Eye24Regular />}
                        style={{ marginTop: "8px", alignSelf: "flex-start" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewDetails(request.id);
                        }}
                      >
                        View Details
                      </Button>
                    </div>
                  }
                />
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Reject Approval Request</DialogTitle>
            <DialogContent>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                }}
              >
                <Field label="Reason for Rejection" required>
                  <Input
                    value={rejectReason}
                    onChange={(_, data) => setRejectReason(data.value)}
                    placeholder="e.g., Insufficient budget, Poor CFROI"
                  />
                </Field>
                <Field label="Additional Notes (Optional)">
                  <Textarea
                    value={rejectNotes}
                    onChange={(_, data) => setRejectNotes(data.value)}
                    placeholder="Add any additional context or instructions..."
                    rows={4}
                  />
                </Field>
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={handleRejectCancel}>
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={handleRejectConfirm}
                disabled={!rejectReason.trim()}
              >
                Confirm Rejection
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
